import React from 'react';
import RatingsTable from './RatingsTable';
import { safeParse, isFailed } from './utils';

export default function GenerationModal({ originalText, originalRatings, genState }) {
  const waiting = !genState || !genState.uuid;
  const done = genState?.status === 'complete';
  const failed = isFailed(genState?.status);
  const data = genState?.data || {};

  const ratingKeys = [
    'rating', 'conceptrating', 'plotrating', 'charactersrating',
    'emotional_impact', 'conflict_level', 'dialoguerating', 'storyforward',
    'characterchanges', 'highstakes', 'internal_goal_score', 'external_goal_score',
    'originality_score', 'engagement_score', 'pacing_score', 'formatting_score',
    'structure_score'
  ];

  const ratingLabels = {
    rating: 'Overall', conceptrating: 'Concept', plotrating: 'Plot',
    charactersrating: 'Characters', emotional_impact: 'Emotional Impact',
    conflict_level: 'Conflict Level', dialoguerating: 'Dialogue',
    storyforward: 'Story Forward', characterchanges: 'Character Changes',
    highstakes: 'High Stakes', internal_goal_score: 'Internal Goal',
    external_goal_score: 'External Goal', originality_score: 'Originality',
    engagement_score: 'Engagement', pacing_score: 'Pacing',
    formatting_score: 'Formatting', structure_score: 'Structure'
  };

  const comments = safeParse(data.comments);
  const parsedRatings = safeParse(data.ratings);

  // Helper function to safely render comments
  const renderComments = () => {
    console.log('GenerationModal comments:', comments, 'Type:', typeof comments, 'IsArray:', Array.isArray(comments));
    
    if (!comments || comments === null || comments === undefined || comments === '' || 
        (Array.isArray(comments) && comments.length === 0) ||
        (typeof comments === 'object' && Object.keys(comments).length === 0)) {
      return null; // Don't render comments section at all
    }

    if (Array.isArray(comments) && comments.every(item => 
      typeof item === 'object' && 
      item.change && 
      item.why_it_helps && 
      item.theory &&
      typeof item.theory === 'object' &&
      item.theory.tags &&
      item.theory.explanation
    )) {
      // Render as sidebar layout for the new nested format
      const SidebarChanges = () => {
        const [selectedIndex, setSelectedIndex] = React.useState(0);
        const selectedItem = comments[selectedIndex];
        
        return (
          <div>
            {/* Changes Layout */}
            <div className="row">
              {/* Left Panel - Changes List */}
              <div className="col-md-5">
                <h6 className="mb-3">Changes</h6>
                <div className="list-group">
                  {comments.map((item, i) => (
                    <div
                      key={i}
                      className={`list-group-item list-group-item-action cursor-pointer ${
                        i === selectedIndex ? 'active' : ''
                      }`}
                      onClick={() => setSelectedIndex(i)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {Array.isArray(item.theory.tags) ? item.theory.tags.map((tag, j) => (
                          <span key={j} className="badge bg-secondary small">{String(tag)}</span>
                        )) : (
                          <span className="badge bg-secondary small">{String(item.theory.tags)}</span>
                        )}
                      </div>
                      <div className="small fw-bold">{String(item.change)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Panel - Details */}
              <div className="col-md-7">
                <div className="border rounded p-3">
                  <h5 className="mb-3">{String(selectedItem.change)}</h5>
                  
                  <div className="mb-4">
                    <h6 className="text-success mb-2">Why It Helps:</h6>
                    <p className="small text-muted mb-0">{String(selectedItem.why_it_helps)}</p>
                  </div>
                  
                  <div className="mb-4">
                    <h6 className="text-info mb-2">Theory & Explanation:</h6>
                    <p className="small text-muted mb-0">{String(selectedItem.theory.explanation)}</p>
                  </div>
                  
                  {selectedItem.theory.example_scenes && (
                    <div>
                      <h6 className="text-warning mb-2">Example Scenes:</h6>
                      <div className="small text-muted">
                        {Array.isArray(selectedItem.theory.example_scenes) ? (
                          <ul className="mb-0">
                            {selectedItem.theory.example_scenes.map((scene, k) => (
                              <li key={k}>{String(scene)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mb-0">{String(selectedItem.theory.example_scenes)}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      };

      return <SidebarChanges />;
    }

    if (Array.isArray(comments) && comments.every(item => 
      typeof item === 'object' && 
      item.change && 
      item.why_it_helps && 
      item.theory &&
      typeof item.theory === 'string'
    )) {
      // Render as table if it's the old change/why/theory format (theory as string)
      return (
        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th style={{width: '25%'}}>Change Made</th>
                <th style={{width: '35%'}}>Why It Helps</th>
                <th style={{width: '40%'}}>Theory & Explanation</th>
              </tr>
            </thead>
            <tbody>
              {comments.map((item, i) => (
                <tr key={i}>
                  <td className="small">{String(item.change)}</td>
                  <td className="small">{String(item.why_it_helps)}</td>
                  <td className="small text-muted">{String(item.theory)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (Array.isArray(comments) && comments.every(item => 
      typeof item === 'object' && 
      item.tags && 
      item.explanation && 
      item.example_scenes
    )) {
      // Render as cards if it's the tags/explanation/example_scenes format
      return (
        <div className="row">
          {comments.map((item, i) => (
            <div key={i} className="col-12 mb-3">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex flex-wrap gap-1">
                    {Array.isArray(item.tags) ? item.tags.map((tag, j) => (
                      <span key={j} className="badge bg-primary">{String(tag)}</span>
                    )) : (
                      <span className="badge bg-primary">{String(item.tags)}</span>
                    )}
                  </div>
                </div>
                <div className="card-body">
                  <p className="card-text small">{String(item.explanation)}</p>
                  {item.example_scenes && (
                    <div className="mt-2">
                      <strong className="small text-muted">Example Scenes:</strong>
                      <div className="small text-muted mt-1">
                        {Array.isArray(item.example_scenes) ? (
                          <ul className="mb-0">
                            {item.example_scenes.map((scene, k) => (
                              <li key={k}>{String(scene)}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mb-0">{String(item.example_scenes)}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (Array.isArray(comments)) {
      // Handle array but different format - safer rendering
      return (
        <div className="alert alert-warning">
          <strong>Unexpected comment format (array):</strong>
          <div className="mt-2">
            {comments.map((item, i) => (
              <div key={i} className="mt-2 p-2 border rounded">
                <small className="text-muted">Item {i + 1}:</small>
                <pre className="small mt-1 mb-0">{JSON.stringify(item, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (typeof comments === 'string') {
      // Handle single string
      return (
        <div className="p-3 border rounded bg-light">
          <p className="mb-0">{comments}</p>
        </div>
      );
    }

    // Handle unexpected object format - safer rendering
    return (
      <div className="alert alert-warning">
        <strong>Unexpected comment format (object):</strong>
        <pre className="small mt-2 mb-0">{JSON.stringify(comments, null, 2)}</pre>
      </div>
    );
  };

  return (
    <div className="modal fade" id="generationModal" tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          {/* ---- Header ---- */}
          <div className="modal-header">
            <h5 className="modal-title">
              {waiting ? 'Waiting to queue…' :
                failed ? 'Generation failed' :
                  done ? `Generation #${genState.uuid}` :
                    'Generating Scene'}
            </h5>
            <button className="btn-close" data-bs-dismiss="modal"></button>
          </div>

          {/* ---- Body ---- */}
          <div className="modal-body">
            <div className="row g-3">
              {/* Original */}
              <div className="col-md-6">
                <h6 className="bg-secondary text-white text-center py-1">
                  Original (at submit)
                </h6>
                <pre className="p-3 border" style={{ whiteSpace: 'pre-wrap' }}>
                  {originalText || ''}
                </pre>
              </div>

              {/* Generated */}
              <div className="col-md-6">
                <h6 className={`${failed ? 'bg-danger' : 'bg-primary'} text-white text-center py-1`}>
                  {failed ? 'Failed' : done ? 'Generated Version' : 'Generating…'}
                </h6>
                {done ? (
                  <pre className="p-3 border" style={{ whiteSpace: 'pre-wrap' }}>
                    {data.scene || ''}
                  </pre>
                ) : failed ? (
                  /* A spinner here was the customer-visible half of the bug: the job had
                     died minutes earlier and the page said "Generating Scene" until they
                     gave up. Say so, and say what to do about it. No credit was charged -
                     RunSceneGeneration returns before the charge on every failure path. */
                  <div className="d-flex flex-column align-items-center p-5 text-center">
                    <i className="fa fa-triangle-exclamation fa-3x mb-3 text-danger" />
                    <p className="mb-1"><strong>This generation didn&rsquo;t finish.</strong></p>
                    <p className="text-muted mb-0">
                      Nothing was charged for it. Try again &mdash; picking a different model
                      often works, since failures are usually specific to one provider.
                    </p>
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center p-5">
                    <i className="fa fa-spinner fa-spin fa-3x mb-3" />
                    <p className="text-muted">
                      This can take up to a minute for regular Models and 2 minutes for DeepSeek Reasoning.
                    </p>
                  </div>
                )}
                {done && (
                  <button 
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={() => navigator.clipboard.writeText(data.scene || '')}
                  >
                    Copy to Clipboard
                  </button>
                )}
              </div>
            </div>

            {/* Ratings */}
            {done && (
              <div className="mt-4">
                <h5>Ratings Comparison</h5>
                <RatingsTable rows={[
                  { label: 'Original', ratings: originalRatings || {} },
                  { label: 'New', ratings: parsedRatings || {} }
                ]} />
              </div>
            )}
            {/* Changes */}
            {done && renderComments() && (
              <div className="mt-4">
                <h5>Changes</h5>
                {renderComments()}
              </div>
            )}

          </div>

          {/* ---- Footer ---- */}
          <div className="modal-footer">
            {done && (
              <button 
                className="btn btn-primary"
                onClick={() => window.dispatchEvent(
                  new CustomEvent('useDraft', { detail: data.scene || '' })
                )}
              >
                Use as Draft
              </button>
            )}
            <button className="btn btn-secondary" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}